import { useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import Button from 'domains/core/components/Button';
import PageHeader from 'domains/core/components/PageHeader';
import { pageLayoutChildrenBoxSx } from 'domains/core/components/PageLayout';
import SettingsHeader, { SettingsSectionHeaderIcon } from 'domains/core/components/SettingsHeader';
import TableHeadText from 'domains/core/components/TableHeadText';
import { useCreateGuardrail, useUpdateGuardrail, useDeleteGuardrail } from 'hooks/mutations/useGuardrailMutations';
import useGetGuardrailsList from 'hooks/queries/useGetGuardrailsList';

import GuardrailFormDialog from './GuardrailFormDialog';

import type { GuardrailBody } from 'hooks/mutations/useGuardrailMutations';
import type { AIGuardrailRow } from 'hooks/queries/useGetGuardrailsList';

const AIGuardrailsPage = () => {
    const { data: guardrails, isLoading, error } = useGetGuardrailsList();
    const [formOpen, setFormOpen] = useState(false);
    const [editingGuardrail, setEditingGuardrail] = useState<AIGuardrailRow | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<AIGuardrailRow | null>(null);

    const createMutation = useCreateGuardrail();
    const updateMutation = useUpdateGuardrail();
    const deleteMutation = useDeleteGuardrail();

    // Tracks which row's toggle is in-flight — used only for per-row loading UI.
    const [togglingId, setTogglingId] = useState<number | null>(null);

    const handleAdd = () => {
        setEditingGuardrail(null);
        setFormOpen(true);
    };

    const handleEdit = (row: AIGuardrailRow) => {
        setEditingGuardrail(row);
        setFormOpen(true);
    };

    const handleFormSubmit = (body: GuardrailBody) => {
        if (editingGuardrail) {
            updateMutation.mutate(
                { id: editingGuardrail.id, ...body },
                {
                    onSuccess: () => {
                        setFormOpen(false);
                        setEditingGuardrail(null);
                    },
                }
            );
        } else {
            createMutation.mutate(body, {
                onSuccess: () => {
                    setFormOpen(false);
                },
            });
        }
    };

    const handleFormClose = () => {
        setFormOpen(false);
        setEditingGuardrail(null);
    };

    const handleDeleteConfirm = () => {
        if (!deleteTarget) return;
        deleteMutation.mutate(
            { id: deleteTarget.id },
            {
                onSuccess: () => {
                    setDeleteTarget(null);
                },
            }
        );
    };

    const handleToggleEnabled = (row: AIGuardrailRow) => {
        setTogglingId(row.id);
        updateMutation.mutate(
            {
                id: row.id,
                guardrail_name: row.guardrail_name,
                feature: row.feature,
                guardrail_type: row.guardrail_type,
                system_prompt: row.system_prompt,
                error_message: row.error_message,
                sort_order: row.sort_order,
                is_enabled: !row.is_enabled,
            },
            { onSettled: () => setTogglingId(null) }
        );
    };

    if (isLoading) {
        return (
            <Box
                sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}
                role="status"
                aria-label="Loading guardrails"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography color="error" role="alert">
                    Failed to load guardrails. Please try again.
                </Typography>
            </Box>
        );
    }

    const headerAction = (
        <Button
            variant="contained"
            onClick={handleAdd}
            data-testid="add-guardrail-button"
            aria-label="Add new guardrail"
        >
            Add Guardrail
        </Button>
    );

    return (
        <Stack data-testid="ai-guardrails-page" aria-label="AI Guardrails">
            <Stack width="100%" height="100%" minHeight={0} mt={8} rowGap={8}>
                <Box sx={{ position: 'relative', width: '100%' }}>
                    <Box
                        sx={(theme) => ({
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            width: '100%',
                            transform: `translateY(calc(-100% - ${theme.spacing(0.5)}))`,
                        })}
                    >
                        <SettingsHeader />
                    </Box>
                    <Stack direction="row" justifyContent="space-between" width="100%">
                        <PageHeader icon={<SettingsSectionHeaderIcon />} header="ai guardrails" />
                        <Stack>{headerAction}</Stack>
                    </Stack>
                </Box>
                <Box sx={pageLayoutChildrenBoxSx}>
                    <TableContainer>
                        <Table aria-label="AI guardrails table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableHeadText>Name</TableHeadText>
                                    </TableCell>
                                    <TableCell>
                                        <TableHeadText>Feature</TableHeadText>
                                    </TableCell>
                                    <TableCell>
                                        <TableHeadText>Type</TableHeadText>
                                    </TableCell>
                                    <TableCell>
                                        <TableHeadText>Sort Order</TableHeadText>
                                    </TableCell>
                                    <TableCell>
                                        <TableHeadText>Enabled</TableHeadText>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TableHeadText>Actions</TableHeadText>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {guardrails && guardrails.length > 0 ? (
                                    guardrails.map((row) => (
                                        <TableRow key={row.id} data-testid={`guardrail-row-${row.id}`}>
                                            <TableCell>{row.guardrail_name}</TableCell>
                                            <TableCell>{row.feature}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.guardrail_type}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: 'action.active',
                                                        color: 'primary.contrastText',
                                                        borderColor: 'action.active',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{row.sort_order ?? '-'}</TableCell>
                                            <TableCell>
                                                <Switch
                                                    checked={row.is_enabled}
                                                    onChange={() => handleToggleEnabled(row)}
                                                    disabled={togglingId === row.id}
                                                    data-testid={`toggle-enabled-${row.id}`}
                                                    aria-label={`${row.is_enabled ? 'Disable' : 'Enable'} ${
                                                        row.guardrail_name
                                                    }`}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: 'action.active',
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            bgcolor: 'action.active',
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    onClick={() => handleEdit(row)}
                                                    data-testid={`edit-guardrail-${row.id}`}
                                                    aria-label={`Edit ${row.guardrail_name}`}
                                                    size="small"
                                                    sx={{ color: 'action.active' }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => setDeleteTarget(row)}
                                                    data-testid={`delete-guardrail-${row.id}`}
                                                    aria-label={`Delete ${row.guardrail_name}`}
                                                    size="small"
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="text.secondary">
                                                No guardrails configured. Click "Add Guardrail" to create one.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Stack>

            <GuardrailFormDialog
                open={formOpen}
                onClose={handleFormClose}
                onSubmit={handleFormSubmit}
                isSubmitting={createMutation.isLoading || updateMutation.isLoading}
                editingGuardrail={editingGuardrail}
            />

            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} aria-labelledby="delete-confirm-title">
                <DialogTitle id="delete-confirm-title">Delete Guardrail</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete "{deleteTarget?.guardrail_name}"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} aria-label="Cancel delete">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={deleteMutation.isLoading}
                        data-testid="confirm-delete-button"
                        aria-label="Confirm delete"
                    >
                        {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default AIGuardrailsPage;
